/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { HotToastService } from '@ngneat/hot-toast';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subject, concat, of } from 'rxjs';
import { ManageCalendarFilterService } from './manage-calendar-filter.service';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { DispatchFilterService } from '../../../../../dispatch-board/src/app/components/dispatch-filter/dispatch-filter.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DispatchFilterOptions } from '../../../../../dispatch-board/src/app/components/dispatch-filter/dispatch-filter.interface';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';

@Component({
  selector: 'app-manage-calendar-filter',
  templateUrl: './manage-calendar-filter.component.html',
  styleUrls: ['./manage-calendar-filter.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ManageCalendarFilterComponent implements OnInit {
  filtersParams: any;
  filterOptions: DispatchFilterOptions = {
    showCloseIcon: false,
    footer: {
      enabled: false
    }
  };
  filterAPIUrls: any = null;
  filtersList: any[] = [];
  mockFiltersList: any[] = [];
  isLoaded: boolean = true;
  searchKey: string = '';
  showSpinner: boolean = false;
  selectedFilter: any = null;
  filterApplicabilityForm: UntypedFormGroup;
  applicabilities: any[] = [{
    applicableType: 'All',
    applicableKey: 'EVERYONE'
  }, {
    applicableType: 'Selected Users',
    applicableKey: 'USERS'
  }, {
    applicableType: 'Selected Teams',
    applicableKey: 'TEAMS'
  }];
  listValue$: Observable<any[]> = new Observable<any[]>();
  preloadedValue: any = [];
  valueSubject$: Subject<string | null> = new Subject<string | null>();
  fieldDetails: any = {
    USERS: {
      dataKey: 'user_uid',
      bindValue: 'user_uid',
      label: 'text.select_users',
      placeholder: 'text.select_users',
      error: 'text.choose_user.atleast_one'
    },
    TEAMS: {
      dataKey: 'team_uid',
      bindValue: 'team_uid',
      label: 'text.select_teams',
      placeholder: 'text.select_teams',
      error: 'text.choose_team.atleast_one'
    }
  };
  lookupLoading: boolean = false;
  pageSize: number = 1;
  moduleName: string = 'JOB_CALENDAR';
  searchTerm: string = '';
  isFilterUpdated: boolean = false;
  isMapViewEnabled: boolean = false;
  dateRange: { fromDate: string; toDate: string } = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialogRef: MatDialogRef<ManageCalendarFilterComponent>,
    private zuperDialogService: ZuperDialogService,
    private dataCollectorService: DataCollectorService,
    private manageCalendarFilterService: ManageCalendarFilterService,
    private dispatchFilterService: DispatchFilterService,
    private toast: HotToastService,
    private utilityService: UtilityService) {
    this.filterApplicabilityForm = new UntypedFormGroup({
      filterName: new UntypedFormControl('', Validators.required),
      filterApplicability: new UntypedFormControl('EVERYONE', Validators.required),
      applicabilityValues: new UntypedFormControl([])
    });
  }

  ngOnInit(): void {
    this.filterAPIUrls = this.manageCalendarFilterService.filterAPIurls;
    this.filtersParams = {
      'count': 50,
      'filter.module': "JOB_CALENDAR",
      'page': 1
    };
    this.getDispatchFilters();
  }

  getDispatchFilters(): void {
    this.isLoaded = false;
    const urlParams: any = this.utilityService.constructParam(this.filtersParams);
    this.dataCollectorService.getData(this.filterAPIUrls.saveFilterUrl, urlParams).subscribe((response) => {
      this.filtersList = response.data;
      this.mockFiltersList = response.data;
      this.selectedFilter = this.filtersList?.length ? this.filtersList[0] : null;
      if (this.selectedFilter) {
        this.onSelectFilter(this.selectedFilter);
      }
      this.isLoaded = true;
    }, (error: any) => {
      this.isLoaded = true;
    });
  }

  onSelectFilter(filter: any): void {
    this.dataCollectorService.getData(this.manageCalendarFilterService.getQuickFilterUrl(filter.filter_uid)).subscribe((response) => {
      this.selectedFilter = response.data;
      this.updateFormValue(response.data);
    });
  }

  updateFormValue(filter: any): void {
    if (filter) {
      let applicabilityValues: any[] = [];
      if (filter.filter_applicability === 'USERS' && filter.filter_applicable_to_users) {
        applicabilityValues = filter.filter_applicable_to_users.map(user => user.user_uid);
        this.preloadedValue = [...filter.filter_applicable_to_users];
      } else if (filter.filter_applicability === 'TEAMS' && filter.filter_applicable_to_users) {
        applicabilityValues = filter.filter_applicable_to_teams.map(team => team.team_uid);
        this.preloadedValue = [...filter.filter_applicable_to_teams];
      }
      if (applicabilityValues?.length) {
        this.loadLookupValues();
      }
      this.filterApplicabilityForm.patchValue({
        filterName: filter.filter_name,
        applicabilityValues: applicabilityValues,
        filterApplicability: filter.filter_applicability
      });
      const applicabilityControl: AbstractControl = this.filterApplicabilityForm.get('applicabilityValues');
      if (['USERS', 'TEAMS'].includes(filter.filter_applicability)) {
        applicabilityControl.setValidators(Validators.required);
      } else {
        applicabilityControl.removeValidators(Validators.required);
      }
      applicabilityControl.updateValueAndValidity();
    }
  }

  triggerSearch(searchKey: string): void {
    this.filtersList = this.mockFiltersList.filter((data: any) => data?.filter_name?.toLocaleLowerCase().includes(searchKey?.toLocaleLowerCase()));
  }

  removeSelectedFilter(): void {
    if (this.selectedFilter) {
      this.openConfirmationBox(this.selectedFilter);
    }
  }

  openConfirmationBox(selectedFilter: any): void {
    const filterName: string = selectedFilter?.filter_name ? `'${selectedFilter?.filter_name}' ` : '';
    const deleteFilter = this.zuperDialogService.open({
      'title': `${this.utilityService.getLocaleValue('text.delete_quick_filter')}`,
      'type': 'error',
      'message': `${this.utilityService.getLocaleValue('delete.quick_filter.confirmation.message')} ${filterName}?`,
      'actions': {
        'confirm': {
          'show': true,
          'label': 'Delete',
          'color': 'error',
          'confirmDisplayKey': 'dispatch.manage_filters.delete.confirm',
        },
        'cancel': {
          'show': true,
          'label': 'Cancel',
          'cancelDisplayKey': 'dispatch.manage_filters.delete.cancel'
        }
      },
      'dismissible': false,
    });
    deleteFilter.afterClosed().subscribe((result) => {
      if (result) {
        this.dataCollectorService.deleteData(this.manageCalendarFilterService.getQuickFilterUrl(selectedFilter.filter_uid)).subscribe((response) => {
          this.toast.success(response.message);
          this.filtersList = [];
          this.isFilterUpdated = true;
          this.getDispatchFilters();
        });
      }
    });
  }

  getFilterValuesCalendar(filterFormValues): any {
    const calendarFilterValue = {
      'filter.keyword': filterFormValues.jobSearch ?? '',
      'filter.job_category': filterFormValues.jobCategory?.toString() ?? '',
      'filter.status': filterFormValues.jobStatus?.toString() ?? '',
      'filter.assigned': filterFormValues.assigned?.toString() ?? '',
      'filter.due_date_from': '',
      'filter.due_date_to': '',
      'filter.from_date': '',
      'filter.to_date': '',
      'filter.skillset_uid': filterFormValues.skills?.toString() ?? '',
      'filter.service_territory': filterFormValues.territory?.toString() ?? '',
      'filter.priority': '',
      'filter.job_tags': filterFormValues.tags?.toString() ?? '',
      'due_date_type': filterFormValues.dueDateType ?? 'ANY',
      'schedule_date_type': filterFormValues.scheduleDateType ?? 'ANY',
      'filter.customer': filterFormValues.customer?.toString() ?? '',
      'filter.customer_organization': filterFormValues.organization?.toString() ?? '',
      'filter.property': filterFormValues.property?.toString() ?? '',
      'filter.assigned_to_team': filterFormValues.team?.toString() ?? '',
      'filter.assigned_to': filterFormValues.employee?.toString() ?? ''
    };

    if (filterFormValues?.dueDateFrom?.format) {
      calendarFilterValue['filter.due_date_from'] = filterFormValues?.dueDateFrom?.format('YYYY-MM-DD');
    }
    if (filterFormValues?.dueDateTo?.format) {
      calendarFilterValue['filter.due_date_to'] = filterFormValues?.dueDateTo?.format('YYYY-MM-DD');
    }
    if (filterFormValues?.scheduleDateFrom?.format) {
      calendarFilterValue['filter.from_date'] = filterFormValues?.scheduleDateFrom?.format('YYYY-MM-DD');
    }
    if (filterFormValues?.scheduleDateTo?.format) {
      calendarFilterValue['filter.to_date'] = filterFormValues?.scheduleDateTo?.format('YYYY-MM-DD');
    }
    if (filterFormValues.priority && filterFormValues.priority !== 'ALL') {
      calendarFilterValue['filter.priority'] = filterFormValues.priority;
    }
    return calendarFilterValue;
  }

  getFilterValues(filterFormValues: any, actualValues: any): any {
    const defaultSortOptions: any = { grid: {}, employee: {}, scheduler: {}, map: {} };
    const filterOptions: any = this.getFilterValuesCalendar(filterFormValues);
    return {
      filterOptions: filterOptions,
      sortOptions: actualValues?.sortOptions ?? defaultSortOptions,
      filterUid: actualValues?.filterUid ?? 'ALL',
    };
  }

  updateFilter(): void {
    this.filterApplicabilityForm.markAllAsTouched();
    if (this.filterApplicabilityForm.valid && this.selectedFilter) {
      this.showSpinner = true;
      const formValue: any = this.filterApplicabilityForm.value;
      const payload: any = {
        quick_filter: {
          filter_applicability: formValue.filterApplicability,
          filter_name: formValue.filterName,
          filter_options: this.getFilterValues(formValue.filterOptions, this.selectedFilter?.filter_options),
          module_name: this.moduleName
        }
      };
      if (formValue.filterApplicability === 'USERS') {
        payload.quick_filter['filter_applicable_to_users'] = formValue.applicabilityValues;
      } else if (formValue.filterApplicability === 'TEAMS') {
        payload.quick_filter['filter_applicable_to_teams'] = formValue.applicabilityValues;
      }
      const toast = this.toast.loading(this.utilityService.getLocaleValue('text.updating_filter'), { id: 'updating_filter_value' });
      this.dataCollectorService.putData(this.manageCalendarFilterService.getQuickFilterUrl(this.selectedFilter.filter_uid), payload).subscribe((response: any) => {
        toast.close();
        this.toast.success(response?.message);
        this.showSpinner = false;
        this.isFilterUpdated = true;
        this.closeManageFilter('save');
      }, (error: any) => {
        toast.close();
        this.showSpinner = false;
      });
    }
  }

  closeManageFilter(type): void {
    this.dialogRef.close({ isFilterUpdated: this.isFilterUpdated, type: type});
  }

  createNewFilter(): void {
    this.dialogRef.close({ createNewFilter: true });
  }

  onSelectionChange(event: any): void {
    this.pageSize = 1;
    this.preloadedValue = [];
    const applicabilityValues: AbstractControl = this.filterApplicabilityForm.get('applicabilityValues');
    if (event.applicableKey && event.applicableKey !== 'EVERYONE') {
      applicabilityValues.setValue([]);
      applicabilityValues.setValidators(Validators.required);
      this.setPreLoadedValueToLookUp();
    } else {
      applicabilityValues.setValue([]);
      applicabilityValues.removeValidators(Validators.required);
    }
    applicabilityValues.updateValueAndValidity();
  }

  appendValueToItems(): void {
    this.pageSize = this.pageSize + 1;
    this.lookupLoading = true;
    this.setPreLoadedValueToLookUp();
  }

  getLookupValueKey(): string {
    return this.fieldDetails[this.filterApplicabilityForm.value?.filterApplicability]?.bindValue ?? '';
  }

  getLookupLabel(): string {
    return this.fieldDetails[this.filterApplicabilityForm.value?.filterApplicability]?.label ?? '';
  }

  getLookupPlacehodler(): string {
    return this.fieldDetails[this.filterApplicabilityForm.value?.filterApplicability]?.placeholder ?? '';
  }

  getLookupError(): string {
    return this.fieldDetails[this.filterApplicabilityForm.value?.filterApplicability]?.error ?? '';
  }

  isInvalidValue(controlName: string): boolean {
    return this.filterApplicabilityForm.get(controlName)?.touched && !this.filterApplicabilityForm.get(controlName)?.valid;
  }

  onItemsClear(): void {
    this.pageSize = 1;
    this.lookupLoading = true;
    this.preloadedValue = [];
    this.setPreLoadedValueToLookUp();
  }

  setPreLoadedValueToLookUp(): any {
    const fieldValueURL = this.filterApplicabilityForm.value?.filterApplicability === 'USERS' ? this.filterAPIUrls.userAllUrl : this.filterAPIUrls.teamsUrl;
    const paramObj = {
      'page': this.pageSize,
      'filter.keyword': this.searchTerm
    };
    return this.dataCollectorService.getData(fieldValueURL, paramObj).subscribe((response: any) => {
      this.preloadedValue = this.preloadedValue.length ? [...this.preloadedValue, ...response.data] :
        response.data;
      this.lookupLoading = false;
      this.loadLookupValues();
    });
  }

  setListValue(term: string = ''): any {
    const fieldValueURL = this.filterApplicabilityForm.value?.filterApplicability === 'USERS' ? this.filterAPIUrls.userAllUrl : this.filterAPIUrls.teamsUrl;
    const paramObj = {
      'page': this.pageSize,
      'filter.keyword': term
    };
    if (fieldValueURL && term) {
      return this.dataCollectorService.getData(fieldValueURL, paramObj)
        .pipe(map((response: any) => {
          this.searchTerm = term;
          this.preloadedValue = response.data;
          return this.preloadedValue;
        }
        ));
    } else {
      this.lookupLoading = false;
    }
  }

  private loadLookupValues(): any {
    this.lookupLoading = false;
    this.listValue$ = concat(
      of(this.preloadedValue), this.valueSubject$.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => this.lookupLoading = true),
        tap(() => this.pageSize = 1),
        switchMap(term => this.setListValue(term).pipe(
          catchError(() => of(this.preloadedValue)),
          tap(() => this.lookupLoading = false)
        ))
      )
    );
  }
}
